import { RootState } from '@/redux/reducers'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useGameUtils } from '@/utils/gameUtils'
import { useEffect, useMemo, useCallback } from 'react'
import { sagaActions } from '@/redux/sagaActions'
import { PayloadFetchTicketDetail } from '@/redux/saga/fetchGameListSaga'
import { createSelector } from '@reduxjs/toolkit'
import { DATE_TIME_FORMAT_HUMAN } from '@/utils/date-time-utils'
import { convertToDateTime } from '@/utils/date-time-utils'
import { useHandleCancel } from './useHandleCancel'
import { getAsTicketStatus } from '@/ui-components/components/TicketStatusCard/utils'
import { toast } from 'react-hot-toast'
import { BetDetailItem } from '@/ui-components/components/TicketDetails'
import { getLottoConfig } from '@/utils/lotto-type-v2'
import { useDialog } from '@/ui-components/components/ConfirmDialog/useDialog'

// Memoized selector outside component
const selectPollDetailData = createSelector(
  [
    (state: RootState) => state.games.ticketDetails,
    (state: RootState) => state.games.loading.ticketDetails,
    (state: RootState) => state.games.response,
  ],
  (pollDetails, loading, response) => ({ pollDetails, loading, response }),
)

const usePollDetailTab = (isHistory?: boolean) => {
  const { getDetailName, getGameName } = useGameUtils()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()
  const { betId } = useParams()
  const title = t('pollItem.details')
  const dialog = useDialog(t)

  const ticketId = betId ? parseInt(betId) : 0
  const { onCancel, checkCanCancel } = useHandleCancel()

  // Use memoized selector
  const { pollDetails, loading, response } = useSelector(selectPollDetailData)
  const configs = getLottoConfig(pollDetails?.lotto_round_game)
  const onBack = () => {
    navigate(-1)
  }

  const onClickCheckResult = useCallback(() => {
    if (!pollDetails?.lotto_game?.type) {
      toast.error(t('message.no_round'))
      return
    }
    if (!pollDetails?.lotto_game?.id) {
      toast.error(t('message.no_round'))
      return
    }
    if (!pollDetails?.lotto_round_game?.id) {
      toast.error(t('message.no_round'))
      return
    }

    navigate(
      `/lotto/${pollDetails.lotto_game.type}/${pollDetails.lotto_game.id}/${pollDetails.lotto_round_game.id}`,
    )
  }, [pollDetails, navigate, t])

  const handleCancel = useCallback(() => {
    if (pollDetails?.id) {
      dialog.confirm(
        t('message.confirmCancelBet', { pollNumber: pollDetails.id }),
        {
          onConfirm: () => onCancel(pollDetails.id),
        },
      )
    }
  }, [pollDetails, dialog, t, onCancel])

  const fetchData = useCallback(() => {
    const payload: PayloadFetchTicketDetail = { ticketId }
    if (isHistory) {
      dispatch({ type: sagaActions.FETCH_TICKET_DETAIL_HISTORY, payload })
      return
    }
    dispatch({ type: sagaActions.FETCH_TICKET_DETAIL, payload })
  }, [dispatch, ticketId, isHistory])

  const ticket = useMemo(() => {
    return {
      ticketNumber: pollDetails?.id,
      betDate: convertToDateTime(
        pollDetails?.created_at ?? '',
        DATE_TIME_FORMAT_HUMAN,
        i18n.language,
      ),
      listTitle: getGameName(pollDetails?.lotto_game),
      drawDate: getDetailName(
        pollDetails?.lotto_game,
        pollDetails?.lotto_round_game,
      ),
      amount: pollDetails?.bet_amount ?? 0,
      winAmount: pollDetails?.win_amount ?? 0,
      status: getAsTicketStatus(
        pollDetails?.status ?? 'waiting',
        pollDetails?.cancellation_reason,
      ), // Resolves the final TicketStatus with cancellation reason
      canCancel: checkCanCancel(pollDetails),
      onCancel: () => handleCancel(),
    }
  }, [
    pollDetails,
    getGameName,
    getDetailName,
    checkCanCancel,
    handleCancel,
    i18n.language,
  ])

  // Get winning numbers from results object
  const results = pollDetails?.results

  // Create bet details array
  const betDetails: BetDetailItem[] = useMemo(() => {
    if (!pollDetails?.details) return []

    return pollDetails.details.map((item, index) => {
      const type = item.type
      const reward = configs.find((c) => c.type === type)?.reward ?? 0
      let limitReward: number | undefined = undefined
      if (reward !== item.reward_amount) {
        limitReward = item.reward_amount
      }

      const status = getAsTicketStatus(
        item.status ?? 'waiting',
        pollDetails?.cancellation_reason,
      )

      // Calculate final payout: effective payout rate × bet amount
      const effectivePayoutRate = limitReward ?? reward
      const betAmount = item.bet_amount ?? 0
      const calculatedFinalPayout = effectivePayoutRate * betAmount

      // Get winning numbers for this bet type from results
      let checkType = type as string
      if (checkType === 'bet_two_under') {
        checkType = 'bet_two_bottom'
      }
      const winningNumbers = results?.[checkType as keyof typeof results]
      // Format winning numbers: join with comma if multiple, or use first one
      const rewardResult =
        winningNumbers && winningNumbers.length > 0
          ? winningNumbers.join(', ')
          : item.reward_result

      return {
        betType: t(`betDetailPanel.betLottoType.${type}`) || type,
        rawBetType: type, // Pass original bet type for internal logic
        rowNumber: index + 1,
        status: status,
        betNumber: item.number,
        rewardResult: rewardResult,
        payoutRate: reward,
        limitedPayoutRate: limitReward,
        betAmount: betAmount,
        finalPayout: calculatedFinalPayout,
      }
    })
  }, [pollDetails, results, configs, t])

  useEffect(() => {
    if (response?.message) {
      if (response.status === 'OK') {
        toast.success(
          t('message.successCancelBet', { pollNumber: response?.data?.id }),
        )
        dispatch({ type: sagaActions.CLEAR_RESPONSE })
        fetchData()
      } else {
        toast.error(response.message)
        dispatch({ type: sagaActions.CLEAR_RESPONSE })
      }
    }
  }, [response, dispatch, navigate, t, fetchData])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    title,
    onBack,
    ticket,
    loading,
    betDetails,
    onClickCheckResult,
    canCancel: checkCanCancel(pollDetails),
    onCancel: handleCancel,
    dialogComponent: dialog.render(),
  }
}

export default usePollDetailTab
